/**
 * Grooming meeting dates and times (epic PI-SPEC-SW-E294, feature
 * PI-SPEC-SW-1507, PKG-PM-F).
 *
 * The one copy of the meeting arithmetic the API and the web preview share,
 * so "Next meeting: Monday 26 October, 10:00" on the Meeting schedule tab is
 * the same instant the API returns as `nextMeetingStart`.
 *
 * - **Dates** (`YYYY-MM-DD`) are calendar dates counted in whole days, never
 *   hours, so a daylight-saving change never moves a meeting to another day.
 *   Only "today" depends on the pattern's time zone.
 * - **Start time** (`HH:mm`, 24-hour) is a wall-clock time in the pattern's
 *   time zone; {@link zonedWallTimeToInstant} turns a date and a start time
 *   into the instant it names there.
 *
 * Pure: no imports, no I/O. `packages/api/src/services/groomingMeetingDate.ts`
 * re-exports the date functions; the web's `lib/grooming/meeting-dates.ts`
 * re-exports {@link previousBusinessDay} as `prepareByDate`.
 */
/** A meeting start time: `HH:mm` on a 24-hour clock, `00:00` to `23:59` (`10:0` and `24:00` are not). */
export const MEETING_START_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
const DAY_MS = 86_400_000;
/** Days since 1970-01-01 of a `YYYY-MM-DD` calendar date (time zone free). */
function dayNumber(date) {
    return Math.round(Date.parse(`${date}T00:00:00Z`) / DAY_MS);
}
function fromDayNumber(day) {
    return new Date(day * DAY_MS).toISOString().slice(0, 10);
}
/** 0 = Sunday ... 6 = Saturday of a day number (1970-01-01 was a Thursday, weekday 4). */
function weekdayOf(day) {
    return (((day + 4) % 7) + 7) % 7;
}
/**
 * `YYYY-MM-DD` of an instant as a calendar date in an IANA time zone.
 * @throws RangeError for a time zone the runtime does not know
 */
export function localDateIn(timeZone, at) {
    // en-CA formats a date as YYYY-MM-DD.
    return new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).format(at);
}
/** Day number of the pattern's first meeting: the first `weekday` on or after the anchor date. */
function firstMeetingDay(pattern) {
    const anchor = dayNumber(pattern.anchorDate);
    return anchor + ((((pattern.weekday - weekdayOf(anchor)) % 7) + 7) % 7);
}
/**
 * The date of the next grooming meeting: the first date on or after today (in
 * the pattern's time zone) that falls on the pattern's weekday, a whole
 * number of `everyNWeeks` weeks from the first such weekday on or after the
 * anchor date. A meeting today counts as the next one.
 *
 * @example nextMeetingDate({ weekday: 4, everyNWeeks: 2, anchorDate: "2026-10-15", timeZone: "America/Chicago" },
 *   new Date("2026-10-16T12:00:00Z")) === "2026-10-29"
 */
export function nextMeetingDate(pattern, now = new Date()) {
    const first = firstMeetingDay(pattern);
    const today = dayNumber(localDateIn(pattern.timeZone, now));
    if (today <= first)
        return fromDayNumber(first);
    const period = pattern.everyNWeeks * 7;
    const steps = Math.ceil((today - first) / period);
    return fromDayNumber(first + steps * period);
}
/**
 * The first meeting date strictly after `date` (`YYYY-MM-DD`). `date` need
 * not be a meeting date itself. Before the pattern's first meeting, that
 * first meeting.
 *
 * @example meetingAfter({ weekday: 1, everyNWeeks: 2, anchorDate: "2026-10-12", timeZone: "America/Chicago" },
 *   "2026-10-26") === "2026-11-09"
 */
export function meetingAfter(pattern, date) {
    const first = firstMeetingDay(pattern);
    const day = dayNumber(date);
    if (day < first)
        return fromDayNumber(first);
    const period = pattern.everyNWeeks * 7;
    const steps = Math.floor((day - first) / period) + 1;
    return fromDayNumber(first + steps * period);
}
/**
 * The next `count` meeting dates, in order: {@link nextMeetingDate}, then the
 * {@link meetingAfter} each one ("the next three meetings as you pick", frame
 * F1). Empty for a `count` below 1.
 */
export function upcomingMeetingDates(pattern, count, now = new Date()) {
    const dates = [];
    if (count < 1)
        return dates;
    let date = nextMeetingDate(pattern, now);
    dates.push(date);
    while (dates.length < Math.floor(count)) {
        date = meetingAfter(pattern, date);
        dates.push(date);
    }
    return dates;
}
/** Milliseconds the zone's wall clock is ahead of UTC at `at` (Chicago in winter: -6 h). */
function zoneOffsetMs(timeZone, at) {
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone,
        hourCycle: "h23",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    }).formatToParts(new Date(at));
    const part = (type) => Number(parts.find((p) => p.type === type)?.value);
    const wallAsUtc = Date.UTC(part("year"), part("month") - 1, part("day"), part("hour"), part("minute"), part("second"));
    return wallAsUtc - (at - (((at % 1000) + 1000) % 1000));
}
/**
 * The instant that wall-clock `time` (`HH:mm`) on `date` (`YYYY-MM-DD`) names
 * in `timeZone`.
 *
 * Two passes of offset correction: the zone's offsets a day before and a day
 * after bracket any daylight-saving change, and each gives a candidate that is
 * kept only if the zone really has that offset at it.
 * - One valid candidate (most days): that instant.
 * - Two (a fall-back hour that happens twice): the earlier one.
 * - None (a spring-forward hour that never happens): the time moved forward by
 *   the gap, so 02:30 on Chicago's spring-forward day is 03:30 CDT.
 *
 * @throws RangeError for a time zone the runtime does not know, or a malformed date or time
 * @example zonedWallTimeToInstant("2026-10-26", "10:00", "America/Chicago").toISOString() === "2026-10-26T15:00:00.000Z"
 */
export function zonedWallTimeToInstant(date, time, timeZone) {
    const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
    const timeMatch = /^(\d{2}):(\d{2})$/.exec(time);
    if (!dateMatch || !timeMatch)
        throw new RangeError(`Expected YYYY-MM-DD and HH:mm, got "${date}" "${time}"`);
    const wallAsUtc = Date.UTC(Number(dateMatch[1]), Number(dateMatch[2]) - 1, Number(dateMatch[3]), Number(timeMatch[1]), Number(timeMatch[2]));
    const before = zoneOffsetMs(timeZone, wallAsUtc - DAY_MS);
    const after = zoneOffsetMs(timeZone, wallAsUtc + DAY_MS);
    const candidates = [...new Set([before, after])]
        .map((offset) => wallAsUtc - offset)
        .filter((instant) => wallAsUtc - zoneOffsetMs(timeZone, instant) === instant)
        .sort((a, b) => a - b);
    const [earliest] = candidates;
    return new Date(earliest ?? wallAsUtc - before);
}
/**
 * The business day before `date` (`YYYY-MM-DD`): one calendar day back, then
 * past Saturday and Sunday, so a Monday gives the Friday before. No holiday
 * calendar. The day to aim to prepare a meeting by (01-roles.md §9.3: ideally
 * one business day before, never forced); the web calls it `prepareByDate`.
 *
 * @example previousBusinessDay("2026-10-26") === "2026-10-23" // Monday → Friday
 */
export function previousBusinessDay(date) {
    let day = dayNumber(date) - 1;
    while (weekdayOf(day) === 6 || weekdayOf(day) === 0)
        day -= 1;
    return fromDayNumber(day);
}
//# sourceMappingURL=meeting-time.js.map